<template>
	<figure v-if="videoUrl" tabindex="0" class="relative" @keydown.space.prevent="togglePlay('video')">
		<!-- error/loading states  -->
		<div
			v-if="playerDisabled"
			class="absolute inset-0 flex items-center justify-center text-gray-300 bg-black bg-opacity-50 rounded-lg dark mb-32 p-16"
		>
			<p v-if="playerState === 'loading'"><BaseIcon name="mono/load" class="mr-8" />Loading...</p>
			<p v-else-if="playerState === 'error'">
				<BaseIcon name="mono/error" class="mr-8" />
				Error playing the video. Please try reloading the page.
			</p>
		</div>

		<!-- animation when clicking on the video to pause/play -->
		<Transition v-if="!playerDisabled" name="flash">
			<div v-if="overlay" class="absolute inset-0 flex items-center justify-center">
				<div class="flex items-center justify-center w-48 h-48 bg-black bg-opacity-50 rounded-full">
					<BaseIcon :name="overlay === 'play' ? 'mono/play' : 'mono/pause'" class="w-32 h-32" />
				</div>
			</div>
		</Transition>

		<video
			ref="player"
			:style="playerStyle"
			:controls="isMobile"
			:controlslist="isMobile ? 'nodownload' : null"
			:playsinline="isMobile"
			class="w-full bg-black border border-gray-600 video-player"
			:class="isMobile ? 'rounded-lg' : 'rounded-t-lg'"
			@click="togglePlay('video')"
			@loadedmetadata="onLoadedMetaData"
			@timeupdate="onTimeUpdate"
			@waiting="onWaiting"
			@canplay="onCanPlay"
			@playing="onPlaying"
			@pause="onPause"
			@ended="onEnded"
			@error="onError"
			@enterpictureinpicture="isPictureInPicture = true"
			@leavepictureinpicture="isPictureInPicture = false"
		>
			<source :src="videoUrl" :type="session.videoMimeType" />
			<p v-if="!isVideoSupported" class="p-20">
				Your browser may not support this video type; please try a different browser (e.g. Chrome or Firefox) if
				it does not play.
			</p>
		</video>

		<!-- CONTROLS -->
		<section
			v-if="!isMobile"
			class="relative w-full pt-12 border border-t-0 border-gray-600 rounded-b-lg bg-gray-650 h-40"
		>
			<!-- progress bar -->
			<VideoPlayerProgressBar class="absolute right-0 left-0" style="bottom: 34px" :disabled="playerDisabled" />
			<!-- toolbar -->
			<div
				class="flex items-center justify-between w-full h-full px-16 pt-8 pb-12"
				style="container-type: inline-size; container-name: controls"
			>
				<div class="flex items-center gap-16">
					<PlayerControlButton
						:disabled="playerDisabled"
						:label="playing ? 'Pause' : 'Play'"
						@click="togglePlay('controls')"
					>
						<BaseIcon v-if="playing" name="mono/pause" class="w-full h-full" />
						<BaseIcon v-else name="mono/play" class="w-full h-full" />
					</PlayerControlButton>
					<PlayerTimeIndicator
						:timeMs="sessionStore.currentTimeSeconds * 1000"
						:durationMs="sessionStore.videoDurationMilliseconds"
						class="text-gray-100"
					/>
				</div>
				<VideoPlayerAudioControl
					v-if="session.mic"
					class="audio-controls"
					:disabled="playerDisabled"
					:volume="volume"
					:muted="muted"
					@update="updateVolume"
					@mute="toggleMute"
				/>
				<div class="flex items-center gap-16">
					<!-- playback rate selector -->
					<VideoPlayerPlaybackRateControl
						:disabled="playerDisabled"
						class="playback-rate-control"
						:playbackRate="playbackRate"
						@update="updatePlaybackRate"
					/>
					<VideoPlayerDownloadControl
						:disabled="playerDisabled"
						:videoUrl="videoUrl"
						class="download-control"
					/>
					<PlayerControlButton
						v-if="pipEnabled"
						class="pip-control"
						:label="isPictureInPicture ? 'Exit picture-in-picture' : 'Enter picture-in-picture'"
						:disabled="playerDisabled"
						@click="togglePictureInPicture"
					>
						<BaseIcon v-if="isPictureInPicture" name="mono/picture-in-picture-exit" class="w-full h-full" />
						<BaseIcon v-else name="mono/picture-in-picture" class="w-full h-full" />
					</PlayerControlButton>
					<PlayerControlButton
						class="fullscreen-control"
						label="Toggle fullscreen"
						:disabled="playerDisabled"
						@click="toggleFullscreen"
					>
						<BaseIcon name="mono/fullscreen" class="w-full h-full" />
					</PlayerControlButton>
				</div>
			</div>
		</section>
	</figure>
</template>

<script>
import * as Sentry from '@sentry/vue'
import {useStorage} from '@vueuse/core'
import insertSessionVideoWithEncodingErrorMutation from 'shared/graphql/insertSessionVideoWithEncodingError.gql'
import PlayerControlButton from './PlayerControlButton.vue'
import PlayerTimeIndicator from './PlayerTimeIndicator.vue'
import VideoPlayerProgressBar from './VideoPlayerProgressBar.vue'
import VideoPlayerAudioControl from './VideoPlayerAudioControl.vue'
import VideoPlayerPlaybackRateControl from './VideoPlayerPlaybackRateControl.vue'
import VideoPlayerDownloadControl from './VideoPlayerDownloadControl.vue'
import {useWindowSize} from 'shared/composables/useWindowSize'
import {useSessionStore} from 'shared/stores/session'

export default {
	name: 'VideoPlayer',
	components: {
		PlayerControlButton,
		PlayerTimeIndicator,
		VideoPlayerProgressBar,
		VideoPlayerAudioControl,
		VideoPlayerPlaybackRateControl,
		VideoPlayerDownloadControl,
	},
	setup() {
		const {isMobile} = useWindowSize()
		return {
			isMobile,
			playbackRate: useStorage('video-playback-rate', 1),
			volume: useStorage('video-volume', 1),
			muted: useStorage('video-muted', false),
			sessionStore: useSessionStore(),
		}
	},
	data() {
		return {
			videoUrl: undefined,
			playing: false,
			ended: false,
			overlay: false,
			isPictureInPicture: false,
			playerState: 'loading',
			errorCount: 0,
			pipEnabled: Boolean(document.pictureInPictureEnabled),
			hasVideoPlaybackFailureBeenPersisted: false,
		}
	},
	computed: {
		session() {
			return this.sessionStore.session
		},
		playerStyle() {
			return {
				'aspect-ratio': `${this.session.videoWidth}/${this.session.videoHeight}`,
				'max-height': this.$sessionLayout.devToolsHidden && '70vh',
			}
		},
		isVideoSupported() {
			// We claim that our mp4 files are supported by any browser,
			// because passing `video/mp4` to `canPlayType` only yields "maybe" in Chromium based browsers.
			// To get precise computed output, we'd have to include the codec, e.g. "video/mp4; codecs="avc1.4d002a"", see:
			// https://developer.mozilla.org/en-US/docs/Web/Media/Formats/codecs_parameter#iso_base_media_file_format_mp4_quicktime_and_3gp
			if (this.session.videoMimeType === 'video/mp4') return true
			return document.createElement('video').canPlayType(this.session.videoMimeType) === 'probably'
		},
		playerDisabled() {
			return this.playerState !== 'ready'
		},
	},
	watch: {
		'sessionStore.seekSeconds': 'updatePlayerTime',
	},
	async created() {
		const result = await this.sessionStore.dataLoader.getVideo(this.session)
		const hasVideo = Boolean(result?.videoUrl)
		this.videoUrl = result?.videoUrl
		document.dispatchEvent(
			new CustomEvent('sessionVideoLoaded', {
				detail: {hasVideo, videoMetadata: result?.videoMetadata, videoEvents: result?.videoEvents},
			})
		)
	},
	mounted() {
		this.$nextTick(() => {
			this.$refs.player?.focus()
		})
	},
	methods: {
		//player methods
		async togglePlay(from) {
			if (this.isMobile) return
			if (this.playing) {
				if (from === 'video') this.flashOverlay('pause')
				this.$refs.player.pause()
				return
			}
			if (from === 'video') this.flashOverlay('play')
			this.$refs.player.currentTime = this.ended ? 0 : this.sessionStore.currentTimeSeconds
			this.ended = false
			try {
				await this.$refs.player.play()
				this.playing = true
			} catch (error) {
				// eslint-disable-next-line no-console
				console.warn('Video playback error', error)
			}
		},
		updateVolume(volume) {
			this.$refs.player.volume = volume
			this.muted = volume === 0
			this.volume = volume
		},
		toggleMute() {
			this.muted = !this.muted
			this.$refs.player.muted = this.muted
		},
		updatePlaybackRate(rate) {
			this.playbackRate = rate
			this.$refs.player.playbackRate = this.playbackRate
		},
		updatePlayerTime(seconds) {
			if (!seconds) return
			if (this.$refs.player) this.$refs.player.currentTime = seconds
		},
		async togglePictureInPicture() {
			if (document.pictureInPictureElement) {
				document.exitPictureInPicture()
				return
			}
			try {
				await this.$refs.player.requestPictureInPicture()
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Picture-in-picture error', error)
				this.$notify.error({
					title: 'There was an error when attempting to enter picture-in-picture',
					message: error.message,
				})
			}
		},
		async toggleFullscreen() {
			// EXIT
			if (document.fullscreenElement) {
				await document.exitFullscreen()
				return
			}
			if (document.webkitFullscreenElement) {
				await document.webkitExitFullscreen()
				return
			}
			// ENTER
			try {
				if (this.$refs.player?.requestFullscreen) {
					await this.$refs.player.requestFullscreen()
					return
				}
				if (this.$refs.player?.webkitRequestFullscreen) {
					await this.$refs.player.webkitRequestFullscreen()
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Fullscreen error', error)
				this.$notify.error({
					title: 'There was an error when attempting to request fullscreen',
					message: error.message,
				})
			}
		},
		// player event handlers
		async onLoadedMetaData(event) {
			const player = event.target
			player.playbackRate = this.playbackRate
			player.volume = this.volume
			player.muted = this.muted
			this.sessionStore.videoDuration = this.session.videoDuration ? this.session.videoDuration : player.duration
			if (this.isMobile) this.playerState = 'ready'
			this.updatePlayerTime(this.sessionStore.seekSeconds)
			const savedTime = this.sessionStore.retrievePlayerTimeFromSessionStorage(this.session.id)
			if (savedTime) {
				this.updatePlayerTime(savedTime)
			}
			window.dispatchEvent(new CustomEvent('playerInitialized'))
		},
		onTimeUpdate(event) {
			const currentTime = event.target.currentTime
			this.sessionStore.currentTimeSeconds = currentTime
			this.sessionStore.storePlayerTimeInSessionStorage(currentTime, this.session.id)
		},
		onPause() {
			this.playing = false
			window.dispatchEvent(new CustomEvent('playbackPause'))
		},
		onEnded() {
			window.dispatchEvent(new CustomEvent('playbackFinished'))
			this.playing = false
			this.ended = true
		},
		onWaiting() {
			this.playerState = 'loading'
		},
		onCanPlay() {
			this.playerState = 'ready'
		},
		onPlaying() {
			this.playing = true
		},
		async onError(event) {
			if (this.playerState === 'error') return
			this.errorCount += 1

			// seek the video forward, to re-attempt loading at a different, hopefully working place
			const aFewSecondsLater = Math.min(Math.round(event.target.currentTime + 2), event.target.duration - 2)
			// Update the video URL to the new time position
			this.videoUrl = `${event.target.currentSrc.split('#t=')[0]}#t=${aFewSecondsLater}`

			if (this.errorCount > 5) {
				// change player state to 'error' after 5 errors
				this.playerState = 'error'

				// eslint-disable-next-line no-console
				console.error('Video playback error after multiple retries.', event.target.error)
				const error = new Error(event.target.error.message, {cause: event.target.error.code})
				Sentry.captureException(error)
				// Keep track of videos with playback issues in the database.
				await this.upsertVideoPlaybackFailure()
			}

			// apply the autoplay setting if not paused
			if (!event.target.paused) {
				event.target.autoplay = true
			}
			// Wait for the next DOM update cycle before reloading the video
			await this.$nextTick()
			// Reset the media element to initial state, rel-loading media;
			// The previously set start time from video URL and the autoplay option (if set) are picked up;
			event.target.load()
		},
		async upsertVideoPlaybackFailure() {
			if (this.hasVideoPlaybackFailureBeenPersisted) return
			if (!this.$store.isUserWorkspaceMember) return
			if (this.session.videoMimeType === 'video/mp4') return
			// eslint-disable-next-line no-console
			console.log('Mark video as having playback issues in Hasura')
			this.hasVideoPlaybackFailureBeenPersisted = true
			try {
				await this.$apollo.mutate({
					mutation: insertSessionVideoWithEncodingErrorMutation,
					variables: {sessionId: this.session.id},
				})
			} catch (error) {
				if (error.graphQLErrors?.some(e => e.extensions?.code === 'constraint-violation')) {
					// eslint-disable-next-line no-console
					console.log('Video has already been marked as having playback issues in Hasura')
					return
				}
				// eslint-disable-next-line no-console
				console.error('Error marking video as having playback issues in Hasura', error)
			}
		},
		// others
		flashOverlay(overlay) {
			if (this.isMobile) return
			// flashes a play/pause overlay for a short time when clicking on the video
			// similar to what youtube is doing, for user feedback
			this.overlay = overlay
			setTimeout(() => {
				this.overlay = false
			}, 10)
		},
	},
}
</script>

<style scoped>
.audio-controls {
	margin: 0 auto 0 16px;
	max-width: 130px;
}

@container controls (max-width: 270px) {
	.audio-controls {
		display: none !important;
	}
	.playback-rate-control {
		display: none !important;
	}
	.pip-control {
		display: none !important;
	}
	.fullscreen-control {
		display: none !important;
	}
}
@container controls (max-width: 350px) {
	.download-control {
		display: none !important;
	}
}

.flash-leave-active {
	transition:
		opacity 0.2s ease,
		transform 0.2s ease;
}

.flash-enter-from {
	opacity: 1;
	transform: scale(1);
}

.flash-leave-to {
	opacity: 0;
	transform: scale(2);
}
</style>
